import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
Tabs,
Tab,
Alert,
LinearProgress,
CircularProgress,
Tooltip,
Menu
} from '@mui/material';
import {
PictureAsPdf,
TableChart,
GetApp,
FilterList,
Refresh,
Today,
Close,
Search,
Download,
TrendingUp,
TrendingDown,
Inventory,
AttachMoney,
Storage,
Warning,
CheckCircle,
Error,
CalendarMonth,
BarChart,
PieChart,
Summarize,
PointOfSale,
Receipt,
AccountBalance,
CompareArrows,
Description,
ShowChart,
Timeline,
ShoppingCart,
People,
Restaurant,
Payment,
ReceiptLong
} from '@mui/icons-material';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {
    APCustomerTypeNames,
    APIEmployeeNames,
APIOrdersReport,
APIOrdersReportDetails,
APIOrdersReportStatistics,
APIOrdersReportTrends
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR } from '../../../services/Text';
import {
formatNumberWithComma2,
getCategoriesNameById,
formatCurrency
} from '../../../utils/helpers';
import {
exportOrdersToExcel,
exportOrdersToPDF,
exportOrdersToCSV
} from '../../../services/OrdersReportService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';

const OrdersReport = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [trendsLoading, setTrendsLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [reportData, setReportData] = useState({
    orders: [],
    summary: {
    total_orders: 0,
    total_sales: 0,
    total_items: 0,
    average_order_value: 0,
    by_payment_method: {},
    by_status: {},
    by_branch: {},
    top_items: []
    },
    trends: [],
    top_customers: [],
    filters_applied: {}
});

const [trendsData, setTrendsData] = useState([]);
const [statisticsData, setStatisticsData] = useState({});

const [filters, setFilters] = useState({
    branch_id: '',
    servant_id: '',
    payment_method: '',
    order_status: '',
    customer_type_id: '',
    date_from: '',
    date_to: '',
    sort_by: 'created_at',
    sort_order: 'desc'
});

const [activeTab, setActiveTab] = useState(0);
const [selectedOrder, setSelectedOrder] = useState(null);
const [detailDialogOpen, setDetailDialogOpen] = useState(false);
const [userRole, setUserRole] = useState('');
const [trendsPeriod, setTrendsPeriod] = useState('daily');
const [trendsRange, setTrendsRange] = useState(30);
const [detailsLoading, setDetailsLoading] = useState(false);
const [referenceData, setReferenceData] = useState({
    branchNames: [],customerType:[],
    employeeNames: [],
    positionNames: []
});

useEffect(() => {
    apiService.setToastFunction(showToast);
}, [showToast]);


useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    fetchReferenceData();
}, []);

useEffect(() => {
    if (activeTab === 0) {
    generateReport();
    }
    fetchStatistics();
}, [activeTab]);

useEffect(() => {
    if (activeTab === 1) {
    fetchTrends();
    }
}, [activeTab, trendsPeriod, trendsRange]);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

const fetchReferenceData = async () => {
    try {
    // Fetch all reference data in parallel
    const [branchesResult, empResult,typResult] = await Promise.all([
        apiService.branchNames(),
        apiService.listNames(APIEmployeeNames),
        apiService.listNames(APCustomerTypeNames),
    ]);

    setReferenceData({
        branchNames: branchesResult.success ? branchesResult.data : [],
        employeeNames: empResult.success ? empResult.data : [],
        customerType: typResult.success ? typResult.data : [],

    });
    } catch (error) {
    showToast("Error", "Failed to load reference data", "error");
    }
};


const generateReport = async () => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        // Get user's branch from session
        branchIdToUse = localStorage.getItem('userBranchId') || '';
    }

    const params = {
        branch_id: branchIdToUse || '',
        servant_id: filters.servant_id || '',
        payment_method: filters.payment_method || '',
        order_status: filters.order_status || '',
        customer_type_id: filters.customer_type_id || '',
        date_from: filters.date_from || '',
        date_to: filters.date_to || '',
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });


    const response = await axios.get(APIOrdersReport, { params });


    if (response.data.status === 'success') {
        const data = response.data.data;
        setReportData({
        orders: data.orders || [],
        summary: data.summary || {
            total_orders: 0,
            total_sales: 0,
            total_items: 0,
            average_order_value: 0,
            by_payment_method: {},
            by_status: {},
            by_branch: {},
            top_items: []
        },
        trends: data.trends || [],
        top_customers: data.top_customers || [],
        filters_applied: data.filters_applied || {}
        });
        showToast("Success", "Orders report generated successfully", "success");
    } else {
        throw new Error(response.data.message || "Failed to generate orders report");
    }
    } catch (error) {
    console.error('Orders API Error:', error);
    let errorMessage = "Failed to generate orders report";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const fetchStatistics = async () => {
    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = localStorage.getItem('userBranchId') || '';
    }

    const params = {
        branch_id: branchIdToUse || '',
        servant_id: filters.servant_id || '',
        date_from: filters.date_from || '',
        date_to: filters.date_to || ''
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });

    const response = await axios.get(APIOrdersReportStatistics, { params });

    if (response.data.status === 'success') {
        setStatisticsData(response.data.data || {});
    }
    } catch (error) {
    console.error('Failed to fetch orders statistics:', error);
    }
};

const fetchTrends = async () => {
    setTrendsLoading(true);
    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = localStorage.getItem('userBranchId') || '';
    }

    const params = {
        period: trendsPeriod,
        range: trendsRange,
        branch_id: branchIdToUse || '',
        servant_id: filters.servant_id || ''
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });

    const response = await axios.get(APIOrdersReportTrends, { params });

    if (response.data.status === 'success') {
        setTrendsData(response.data.data.trends || []);
    } else {
        setTrendsData([]);
    }
    } catch (error) {
    console.error('Failed to fetch orders trends:', error);
    setTrendsData([]);
    } finally {
    setTrendsLoading(false);
    }
};

const fetchOrderDetails = async (orderId) => {
    try {
    const response = await axios.get(`${APIOrdersReportDetails}/${orderId}`);
    
    if (response.data.status === 'success') {
        const data = response.data.data;
        
        console.log('Order Details Response:', data);
        
        if (data.order) {
        return {
            order: data.order,
            items: data.items || [],
            customer: data.customer,
            summary: data.summary,
            ...data.order
        };
        } else if (data.order_number) {
        return data;
        } else {
        return {
            order_number: data.order_number,
            transaction_id: data.transaction_id,
            created_at: data.created_at,
            branch_id: data.branch_id,
            servant_id: data.servant_id,
            customer_id: data.customer_id,
            customer_type_id: data.customer_type_id,
            total_amount: data.total_amount || 0,
            payment_method: data.payment_method || 'pending',
            order_status: data.order_status || 'pending',
            order_notes: data.order_notes || '',
            payment_notes: data.payment_notes || '',
            items: data.items || [],
            customer: data.customer || { name: 'Walk-in Customer' },
            servant: data.servant || { name: 'N/A' },
            customer_type: data.customer_type || { name: 'N/A' }
        };
        }
    } else {
        throw new Error("Failed to fetch order details");
    }
    } catch (error) {
    console.error('Failed to fetch order details:', error);
    showToast("Error", "Failed to load order details", "error");
    return null;
    }
};

const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const handleDateRangeChange = (type, value) => {
    setFilters(prev => ({
    ...prev,
    [type]: value
    }));
};

const applyToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    date_from: today,
    date_to: today
    });
};

const applyThisWeek = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    
    setFilters({
    ...filters,
    date_from: firstDay.toISOString().split('T')[0],
    date_to: lastDay.toISOString().split('T')[0]
    });
};

const applyThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
    ...filters,
    date_from: firstDay.toISOString().split('T')[0],
    date_to: lastDay.toISOString().split('T')[0]
    });
};

const clearFilters = () => {
    setFilters({
    branch_id: '',
    servant_id: '',
    payment_method: '',
    order_status: '',
    customer_type_id: '',
    date_from: '',
    date_to: '',
    sort_by: 'created_at',
    sort_order: 'desc'
    });
};

const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportOrdersToExcel(reportData, filters);
    showToast("Success", "Excel report downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportOrdersToPDF(reportData, filters);
    showToast("Success", "PDF report downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportOrdersToCSV(reportData, filters);
    showToast("Success", "CSV report downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getStatusColor = (status) => {
    if (!status) return theme.palette.grey[500];

    const statusLower = status.toLowerCase();
    switch (statusLower) {
    case 'completed': return theme.palette.success.main;
    case 'pending': return theme.palette.warning.main;
    case 'cancelled': return theme.palette.error.main;
    case 'quotation': return theme.palette.info.main;
    default: return theme.palette.grey[500];
    }
};

const getPaymentMethodColor = (method) => {
    if (!method) return theme.palette.grey[500];

    const methodLower = method.toLowerCase();
    switch (methodLower) {
    case 'cash': return theme.palette.success.main;
    case 'mobile_money': return theme.palette.info.main;
    case 'bank': return theme.palette.primary.main;
    case 'pending': return theme.palette.warning.main;
    default: return theme.palette.grey[500];
    }
};

const handleViewDetails = async (order) => {
    setDetailsLoading(true);
    try {
    const details = await fetchOrderDetails(order.id);
    if (details) {
        const enrichedOrder = {
        ...order,
        order_number: details.order_number || details.order?.order_number || order.order_number,
        transaction_id: details.transaction_id || details.order?.transaction_id || order.transaction_id,
        created_at: details.created_at || details.order?.created_at || order.created_at,
        branch_id: details.branch_id || details.order?.branch_id || order.branch_id,
        servant_id: details.servant_id || details.order?.servant_id || order.servant_id,
        customer_id: details.customer_id || details.order?.customer_id || order.customer_id,
        customer_type_id: details.customer_type_id || details.order?.customer_type_id || order.customer_type_id,
        total_amount: details.total_amount || details.order?.total_amount || order.total_amount || 0,
        payment_method: details.payment_method || details.order?.payment_method || order.payment_method || 'pending',
        order_status: details.order_status || details.order?.order_status || order.order_status || 'pending',
        order_notes: details.order_notes || details.order?.order_notes || order.order_notes || '',
        payment_notes: details.payment_notes || details.order?.payment_notes || order.payment_notes || '',
        items: details.items || details.order?.items || order.items || [],
        customer: details.customer || details.order?.customer || order.customer || { name: 'Walk-in Customer' },
        servant: details.servant || details.order?.servant || order.servant || { name: 'N/A' },
        customer_type: details.customer_type || details.order?.customer_type || order.customer_type || { name: 'N/A' }
        };
        setSelectedOrder(enrichedOrder);
    } else {
        const fallbackOrder = {
        ...order,
        items: order.items || [],
        customer: order.customer || { name: 'Walk-in Customer' }
        };
        setSelectedOrder(fallbackOrder);
    }
    setDetailDialogOpen(true);
    } catch (error) {
    console.error('Error in handleViewDetails:', error);
    showToast("Error", "Failed to load order details", "error");
    const fallbackOrder = {
        ...order,
        items: order.items || [],
        customer: order.customer || { name: 'Walk-in Customer' }
    };
    setSelectedOrder(fallbackOrder);
    setDetailDialogOpen(true);
    } finally {
    setDetailsLoading(false);
    }
};

const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

const handleTrendsPeriodChange = (period) => {
    setTrendsPeriod(period);
};

const handleTrendsRangeChange = (range) => {
    setTrendsRange(range);
};

const formatOrderNumber = (orderNumber) => {
    if (!orderNumber) return 'N/A';
    return orderNumber.replace(/(.{4})/g, '$1-').slice(0, -1);
};

if (loading && !reportData.orders.length && activeTab === 0) {
    return (
    <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box textAlign="center">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Generating Orders Report...</Typography>
        </Box>
    </Container>
    );
}

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Orders Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Comprehensive orders analysis and sales management
        </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={generateReport}
            disabled={activeTab !== 0}
        >
            Refresh Report
        </Button>
        <Button
            variant="contained"
            color="success"
            startIcon={<GetApp />}
            onClick={handleExportClick}
            disabled={!reportData.orders || reportData.orders.length === 0}
        >
            Export Report
        </Button>
        </Box>
    </Box>

    {/* Filters Section */}
    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterList /> Report Filters
        </Typography>
        
        <Grid container spacing={3}>
        {/* Quick Date Filters */}
        <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
                variant="outlined"
                startIcon={<Today />}
                onClick={applyToday}
            >
                Today
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisWeek}
            >
                This Week
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisMonth}
            >
                This Month
            </Button>
            <Button
                variant="outlined"
                color="error"
                startIcon={<Close />}
                onClick={clearFilters}
            >
                Clear All
            </Button>
            </Box>
        </Grid>

        {/* Branch Filter */}
        {hasPermission(PERMISSIONS.BRANCHES) && (
            <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                name="branch_id"
                value={filters.branch_id}
                onChange={handleFilterChange}
                label="Branch"
                >
                <MenuItem value="">All Branches</MenuItem>
                {referenceData.branchNames.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                    </MenuItem>
                ))}
                </Select>
            </FormControl>
            </Grid>
        )}


        {/* Servant/Employee Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Servant</InputLabel>
            <Select
                name="servant_id"
                value={filters.servant_id}
                onChange={handleFilterChange}
                label="Servant"
            >
                <MenuItem value="">All Servants</MenuItem>
                {referenceData.employeeNames.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                    {employee.employee_name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Grid>

        {/* Payment Method Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Payment Method</InputLabel>
            <Select
                name="payment_method"
                value={filters.payment_method}
                onChange={handleFilterChange}
                label="Payment Method"
            >
                <MenuItem value="">All Methods</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Order Status Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Order Status</InputLabel>
            <Select
                name="order_status"
                value={filters.order_status}
                onChange={handleFilterChange}
                label="Order Status"
            >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="quotation">Quotation</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Customer Type Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Customer Type</InputLabel>
            <Select
                name="customer_type_id"
                value={filters.customer_type_id}
                onChange={handleFilterChange}
                label="Customer Type"
            >
                <MenuItem value="">All Types</MenuItem>
                {referenceData.customerType.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                    {type.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Grid>

        {/* Date Range */}
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="From Date"
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={(e) => handleDateRangeChange('date_from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="To Date"
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={(e) => handleDateRangeChange('date_to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>

        {/* Sort Options */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
                name="sort_by"
                value={filters.sort_by}
                onChange={handleFilterChange}
                label="Sort By"
            >
                <MenuItem value="created_at">Order Date</MenuItem>
                <MenuItem value="total_amount">Total Amount</MenuItem>
                <MenuItem value="order_number">Order Number</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort Order</InputLabel>
            <Select
                name="sort_order"
                value={filters.sort_order}
                onChange={handleFilterChange}
                label="Sort Order"
            >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Generate Button */}
        <Grid item xs={12}>
            <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={generateReport}
            startIcon={<Search />}
            sx={{ mt: 2 }}
            >
            Generate Report
            </Button>
        </Grid>
        </Grid>

        {/* Active Filters */}
        {Object.keys(filters).some(key => filters[key] && !['sort_by', 'sort_order'].includes(key)) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Active Filters:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.branch_id && (
                <Chip
                label={`Branch: ${getCategoriesNameById(filters.branch_id,referenceData.branchNames)}`}
                onDelete={() => handleFilterChange({ target: { name: 'branch_id', value: '' } })}
                color="primary"
                size="small"
                />
            )}

            {filters.servant_id && (
                <Chip
                label={`Servant: ${getCategoriesNameById(filters.servant_id, referenceData.employeeNames)}`}
                onDelete={() => handleFilterChange({ target: { name: 'servant_id', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.payment_method && (
                <Chip
                label={`Payment: ${filters.payment_method.charAt(0).toUpperCase() + filters.payment_method.slice(1)}`}
                onDelete={() => handleFilterChange({ target: { name: 'payment_method', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.order_status && (
                <Chip
                label={`Status: ${filters.order_status.charAt(0).toUpperCase() + filters.order_status.slice(1)}`}
                onDelete={() => handleFilterChange({ target: { name: 'order_status', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.date_from && filters.date_to && (
                <Chip
                label={`Date: ${filters.date_from} to ${filters.date_to}`}
                onDelete={() => {
                    handleFilterChange({ target: { name: 'date_from', value: '' } });
                    handleFilterChange({ target: { name: 'date_to', value: '' } });
                }}
                color="primary"
                size="small"
                />
            )}
            </Box>
        </Box>
        )}
    </Paper>

    {/* Summary Cards */}
    {reportData.summary && reportData.summary.total_orders > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Orders */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <ReceiptLong sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma2(reportData.summary.total_orders || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Orders
                </Typography>
                {statisticsData.today && (
                <Typography variant="caption" color="primary">
                    Today: {statisticsData.today.count || 0}
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>

        {/* Total Sales */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <PointOfSale sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_sales || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Sales
                </Typography>
                {statisticsData.today && (
                <Typography variant="caption" color="success">
                    Today: {formatCurrency(statisticsData.today.total_sales || 0)}
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>

        {/* Total Items Sold */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <ShoppingCart sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma2(reportData.summary.total_items || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Items Sold
                </Typography>
                {reportData.summary.total_orders > 0 && (
                <Typography variant="caption" color="info">
                    Avg: {formatNumberWithComma2(Math.round(reportData.summary.total_items / reportData.summary.total_orders))} per order
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>

        {/* Average Order Value */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <AttachMoney sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.average_order_value || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Avg Order Value
                </Typography>
                {statisticsData.today && (
                <Typography variant="caption" color="warning">
                    Today: {formatCurrency(statisticsData.today.avg_order_value || 0)}
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>

        {/* Completed Orders */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma2(reportData.summary.by_status?.completed?.count || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Completed
                </Typography>
                <Typography variant="caption" color="success">
                {reportData.summary.total_orders > 0 ? 
                    Math.round((reportData.summary.by_status?.completed?.count || 0) / reportData.summary.total_orders * 100) : 0}%
                </Typography>
            </CardContent>
            </Card>
        </Grid>

        {/* Pending Orders */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Warning sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma2(reportData.summary.by_status?.pending?.count || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Pending
                </Typography>
                <Typography variant="caption" color="warning">
                {reportData.summary.total_orders > 0 ? 
                    Math.round((reportData.summary.by_status?.pending?.count || 0) / reportData.summary.total_orders * 100) : 0}%
                </Typography>
            </CardContent>
            </Card>
        </Grid>
        </Grid>
    )}

    {/* Error Message */}
    {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        </Alert>
    )}

    {/* Report Tabs */}
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ bgcolor: theme.palette.primary.main }}
        >
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Summarize /> Orders List
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <ShowChart /> Sales Trends
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <PieChart /> Analysis
            </Box>
            }
        />
        </Tabs>

        {/* Tab 1: Orders List */}
        {activeTab === 0 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Orders List
            </Typography>
            
            {reportData.orders && reportData.orders.length > 0 ? (
            <>
                <TableContainer>
                <Table>
                    <TableHead>
                    <TableRow sx={{
                        backgroundColor: theme.palette.primary.main,
                        '& th': {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.95rem'
                        }
                    }}>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Order No.</strong></TableCell>
                        <TableCell><strong>Date/Time</strong></TableCell>
                        <TableCell><strong>Customer</strong></TableCell>
                        <TableCell><strong>Servant</strong></TableCell>
                        <TableCell><strong>Total Amount</strong></TableCell>
                        <TableCell><strong>Payment</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {reportData.orders.map((order, index) => (
                        <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 'medium' }}>
                            {formatOrderNumber(order.order_number)}
                        </TableCell>
                        <TableCell>
                            {new Date(order.created_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                            })}
                        </TableCell>
                        <TableCell>
                            {order.customer_id}
                            {order.customer_type && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                ({order.customer_type.name})
                            </Typography>
                            )}
                        </TableCell>
                        <TableCell>{order.servant?.name || getCategoriesNameById(order.servant_id, referenceData.employeeNames)}</TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.dark }}>
                            {formatCurrency(order.total_amount)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip
                            label={order.payment_method ? order.payment_method.replace('_', ' ').toUpperCase() : 'PENDING'}
                            size="small"
                            sx={{
                                bgcolor: getPaymentMethodColor(order.payment_method),
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                            />
                        </TableCell>
                        <TableCell>
                            <Chip
                            label={order.order_status ? order.order_status.toUpperCase() : 'PENDING'}
                            size="small"
                            sx={{
                                bgcolor: getStatusColor(order.order_status),
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                            />
                        </TableCell>
                        <TableCell>
                            <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewDetails(order)}
                            disabled={!order || !order.id}
                            >
                            View Items
                            </Button>
                        </TableCell>
                        </TableRow>
                    ))}
                    
                    {/* Footer Row */}
                    {reportData.orders.length > 0 && (
                        <TableRow sx={{ 
                        backgroundColor: 'black',
                        '& td': {
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            borderTop: `3px solid ${theme.palette.primary.main}`,
                            padding: '16px 8px'
                        }
                        }}>
                        <TableCell colSpan={5} align="center">
                            <Typography variant="h6" sx={{ color: 'white' }}>
                            GRAND TOTALS
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="h6" sx={{ 
                            color: 'white',
                            backgroundColor: theme.palette.success.dark,
                            padding: '8px 12px',
                            borderRadius: '4px',
                            display: 'inline-block'
                            }}>
                            {formatCurrency(reportData.summary.total_sales)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ color: 'white' }}>
                            {reportData.summary.by_payment_method && 
                                Object.entries(reportData.summary.by_payment_method).map(([method, data]) => (
                                <div key={method}>
                                    {method.toUpperCase()}: {formatCurrency(data.total_amount)}
                                </div>
                                ))}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ color: 'white' }}>
                            {reportData.summary.by_status && 
                                Object.entries(reportData.summary.by_status).map(([status, data]) => (
                                <div key={status}>
                                    {status.toUpperCase()}: {data.count}
                                </div>
                                ))}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip
                            label="SUMMARY"
                            sx={{
                                backgroundColor: theme.palette.primary.dark,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.875rem',
                                height: '32px'
                            }}
                            />
                        </TableCell>
                        </TableRow>
                    )}
                    </TableBody>
                </Table>
                </TableContainer>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {reportData.orders.length} orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Generated on: {new Date().toLocaleString()}
                </Typography>
                </Box>
            </>
            ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptLong sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No orders found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting your filters
                </Typography>
                <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={generateReport}
                >
                Refresh Data
                </Button>
            </Box>
            )}
        </Box>
        )}

        {/* Tab 2: Sales Trends */}
        {activeTab === 1 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChart /> Sales Trends
            </Typography>
            
            {/* Trends Filters */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                    <InputLabel>Period</InputLabel>
                    <Select
                    value={trendsPeriod}
                    onChange={(e) => handleTrendsPeriodChange(e.target.value)}
                    label="Period"
                    >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                    <InputLabel>Range</InputLabel>
                    <Select
                    value={trendsRange}
                    onChange={(e) => handleTrendsRangeChange(e.target.value)}
                    label="Range"
                    >
                    <MenuItem value={7}>7 Days</MenuItem>
                    <MenuItem value={30}>30 Days</MenuItem>
                    <MenuItem value={90}>90 Days</MenuItem>
                    <MenuItem value={180}>6 Months</MenuItem>
                    <MenuItem value={365}>1 Year</MenuItem>
                    </Select>
                </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={fetchTrends}
                    startIcon={<Refresh />}
                    disabled={trendsLoading}
                >
                    {trendsLoading ? 'Loading...' : 'Update Trends'}
                </Button>
                </Grid>
            </Grid>
            </Paper>

            {trendsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
            ) : trendsData.length > 0 ? (
            <Grid container spacing={3}>
                {/* Sales Trends */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Timeline /> Sales Trends
                    </Typography>
                    <Box sx={{ height: 300, overflow: 'auto' }}>
                        {trendsData.map((trend, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                {trend.period}
                            </Typography>
                            <Typography variant="h6" color="primary">
                                {formatCurrency(trend.total_sales)}
                            </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Orders: {trend.order_count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avg: {formatCurrency(trend.order_count > 0 ? trend.total_sales / trend.order_count : 0)}
                            </Typography>
                            </Box>
                            <LinearProgress 
                            variant="determinate" 
                            value={Math.min((trend.total_sales / Math.max(...trendsData.map(t => t.total_sales), 1)) * 100, 100)}
                            sx={{ mt: 1, height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        ))}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>

                {/* Order Count Trends */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptLong /> Order Volume
                    </Typography>
                    <Box sx={{ height: 300, overflow: 'auto' }}>
                        {trendsData.map((trend, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                {trend.period}
                            </Typography>
                            <Typography variant="h6" color="secondary">
                                {trend.order_count} orders
                            </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Items: {trend.total_items || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avg: {trend.order_count > 0 ? Math.round((trend.total_items || 0) / trend.order_count) : 0} items/order
                            </Typography>
                            </Box>
                            <LinearProgress 
                            variant="determinate" 
                            value={Math.min((trend.order_count / Math.max(...trendsData.map(t => t.order_count), 1)) * 100, 100)}
                            color="secondary"
                            sx={{ mt: 1, height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        ))}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>
            </Grid>
            ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <ShowChart sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No trends data available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting the filters or generate report first
                </Typography>
                <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => setActiveTab(0)}
                >
                Go to Orders List
                </Button>
            </Box>
            )}
        </Box>
        )}

        {/* Tab 3: Analysis */}
        {activeTab === 2 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Orders Analysis
            </Typography>
            
            {reportData.summary && reportData.summary.total_orders > 0 ? (
            <Grid container spacing={3}>
                {/* Status Distribution */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        Order Status Distribution
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        {Object.entries(reportData.summary.by_status || {}).map(([status, data], index) => {
                        const percentage = reportData.summary.total_orders > 0 ? 
                            (data.count / reportData.summary.total_orders) * 100 : 0;
                        
                        return (
                            <Box key={status} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {status}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {data.count} ({percentage.toFixed(1)}%)
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={percentage}
                                sx={{ 
                                height: 10, 
                                borderRadius: 5,
                                backgroundColor: theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: getStatusColor(status),
                                    borderRadius: 5
                                }
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                Sales: {formatCurrency(data.total_amount)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                Avg: {formatCurrency(data.count > 0 ? data.total_amount / data.count : 0)}
                                </Typography>
                            </Box>
                            </Box>
                        );
                        })}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>

                {/* Payment Method Distribution */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        Payment Method Distribution
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        {Object.entries(reportData.summary.by_payment_method || {}).map(([method, data], index) => {
                        const percentage = reportData.summary.total_orders > 0 ? 
                            (data.count / reportData.summary.total_orders) * 100 : 0;
                        
                        return (
                            <Box key={method} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {method.replace('_', ' ')}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {data.count} ({percentage.toFixed(1)}%)
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={percentage}
                                sx={{ 
                                height: 10, 
                                borderRadius: 5,
                                backgroundColor: theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: getPaymentMethodColor(method),
                                    borderRadius: 5
                                }
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                Amount: {formatCurrency(data.total_amount)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                Avg: {formatCurrency(data.count > 0 ? data.total_amount / data.count : 0)}
                                </Typography>
                            </Box>
                            </Box>
                        );
                        })}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>

                {/* Top Items */}
                {reportData.summary.top_items && reportData.summary.top_items.length > 0 && (
                <Grid item xs={12}>
                    <Card elevation={2}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                        Top Selling Items
                        </Typography>
                        <TableContainer>
                        <Table size="small">
                            <TableHead>
                            <TableRow>
                                <TableCell><strong>Item</strong></TableCell>
                                <TableCell align="right"><strong>Quantity Sold</strong></TableCell>
                                <TableCell align="right"><strong>Total Sales</strong></TableCell>
                                <TableCell align="right"><strong>Avg Price</strong></TableCell>
                                <TableCell align="right"><strong>Orders</strong></TableCell>
                            </TableRow>
                            </TableHead>
                            <TableBody>
                            {reportData.summary.top_items.map((item, index) => (
                                <TableRow key={index}>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {item.food_name || item.name || 'Unknown Item'}
                                    </Typography>
                                    {item.food_type && (
                                    <Typography variant="caption" color="text.secondary">
                                        Type: {item.food_type}
                                    </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatNumberWithComma2(item.total_quantity)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.dark }}>
                                    {formatCurrency(item.total_sales)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2">
                                    {formatCurrency(item.avg_price || 0)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2">
                                    {item.order_count}
                                    </Typography>
                                </TableCell>
                                </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                        </TableContainer>
                    </CardContent>
                    </Card>
                </Grid>
                )}

                {/* Top Customers */}
                {reportData.top_customers && reportData.top_customers.length > 0 && (
                <Grid item xs={12}>
                    <Card elevation={2}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                        Top Customers
                        </Typography>
                        <Grid container spacing={2}>
                        {reportData.top_customers.map((customer, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                            <Card sx={{ 
                                bgcolor: 'primary',
                                borderLeft: `4px solid ${theme.palette.primary.main}`
                            }}>
                                <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {customer.customer_name || 'Walk-in Customer'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {customer.customer_type || 'N/A'}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                    Orders: <strong>{customer.order_count}</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                    Total Spent: <strong>{formatCurrency(customer.total_spent)}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                    Avg Order: {formatCurrency(customer.avg_order_value)}
                                    </Typography>
                                </Box>
                                </CardContent>
                            </Card>
                            </Grid>
                        ))}
                        </Grid>
                    </CardContent>
                    </Card>
                </Grid>
                )}
            </Grid>
            ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <PieChart sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No analysis data available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Generate a report first to see analysis
                </Typography>
                <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => setActiveTab(0)}
                >
                Go to Orders List
                </Button>
            </Box>
            )}
        </Box>
        )}
    </Paper>

    {/* Details Dialog */}
    <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
    >
        <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
            Order Details - {selectedOrder ? formatOrderNumber(selectedOrder.order_number) : ''}
            </Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
            <Close />
            </IconButton>
        </Box>
        </DialogTitle>
        <DialogContent dividers>
        {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading order details...</Typography>
            </Box>
        ) : selectedOrder ? (
            <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Order Information</Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Order Number:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {formatOrderNumber(selectedOrder.order_number)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Transaction ID:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedOrder.transaction_id || 'N/A'}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Date/Time:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {new Date(selectedOrder.created_at).toLocaleString()}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Customer:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedOrder.customer?.name || 'Walk-in Customer'}
                        </Typography>
                        {selectedOrder.customer_type && (
                        <Typography variant="caption" color="text.secondary">
                            ({selectedOrder.customer_type.name})
                        </Typography>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Servant:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {selectedOrder.servant?.name || getCategoriesNameById(selectedOrder.servant_id, referenceData.employeeNames)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                        <Chip
                        label={selectedOrder.payment_method ? selectedOrder.payment_method.replace('_', ' ').toUpperCase() : 'PENDING'}
                        sx={{
                            bgcolor: getPaymentMethodColor(selectedOrder.payment_method),
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip
                        label={selectedOrder.order_status ? selectedOrder.order_status.toUpperCase() : 'PENDING'}
                        sx={{
                            bgcolor: getStatusColor(selectedOrder.order_status),
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                        />
                    </Grid>
                    </Grid>
                </CardContent>
                </Card>
            </Grid>

            {/* Financial Summary */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Financial Summary</Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.success.dark }}>
                        {formatCurrency(selectedOrder.total_amount)}
                        </Typography>
                    </Grid>
                    {selectedOrder.payment_notes && (
                        <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Payment Notes:</Typography>
                        <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                            {selectedOrder.payment_notes}
                        </Typography>
                        </Grid>
                    )}
                    {selectedOrder.order_notes && (
                        <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Order Notes:</Typography>
                        <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                            {selectedOrder.order_notes}
                        </Typography>
                        </Grid>
                    )}
                    </Grid>
                </CardContent>
                </Card>
            </Grid>

            {/* Items Table */}
            {selectedOrder.items && selectedOrder.items.length > 0 && (
                <Grid item xs={12}>
                <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Order Items</Typography>
                    <TableContainer>
                        <Table size="small">
                        <TableHead>
                            <TableRow sx={{
                            backgroundColor: theme.palette.primary.main,
                            '& th': {
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.95rem'
                            }
                            }}>
                            <TableCell>Item Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedOrder.items.map((item, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                {item.food?.name || item.food_name || 'N/A'}
                                {item.food && item.food.description && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                    {item.food.description}
                                    </Typography>
                                )}
                                </TableCell>
                                <TableCell>
                                <Chip
                                    label={item.food_type_id === 'ki' ? 'Kitchen' : 'Other'}
                                    size="small"
                                    color={item.food_type_id === 'ki' ? 'primary' : 'secondary'}
                                />
                                </TableCell>
                                <TableCell align="right">{formatNumberWithComma2(item.quantity)}</TableCell>
                                <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(item.total_amount)}
                                </Typography>
                                </TableCell>
                            </TableRow>
                            ))}
                            {/* Total Row */}
                            <TableRow sx={{ 
                            backgroundColor: 'action.hover',
                            '& td': {
                                fontWeight: 'bold',
                                borderTop: `2px solid ${theme.palette.divider}`
                            }
                            }}>
                            <TableCell colSpan={4} align="right">
                                <Typography variant="body1">Total:</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="h6" color="success.dark">
                                {formatCurrency(selectedOrder.items.reduce((sum, item) => sum + (item.total_amount || 0), 0))}
                                </Typography>
                            </TableCell>
                            </TableRow>
                        </TableBody>
                        </Table>
                    </TableContainer>
                    </CardContent>
                </Card>
                </Grid>
            )}
            </Grid>
        ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No order details available</Typography>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setDetailDialogOpen(false)} color="primary">
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>
    </Container>
);
};

export default OrdersReport;

